import { equals, pathOr } from 'ramda'
import React from 'react'
import { useSelector } from 'react-redux'

import { Groups } from '@/app/component/conditions/definitions'
import { useCampaignFiles } from '@/app/module/campaigns/component/campaign-files-provider'
import { BaseLanguageType } from '@/app/module/campaigns/helpers'
import { selectMessagesState } from '@/app/module/campaigns/store/selectors'
import { CampaignFileResult, ConditionActions, IVRCampaignItem, MessagesState } from '@/app/module/campaigns/types'
import { CountryTimezone, FileUploadType, TimezoneWithCountryCodeType } from '@/app/types'

import Reconnect from './reconnect'

type Props = {
  countriesTimezones: CountryTimezone[]
  countryCode: string
  groups: Groups
  item: IVRCampaignItem
  itemId: number
  loading: boolean
  orgId: number
  segments: Groups
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string
  voiceList: BaseLanguageType[]

  getFile: (payload: { file: string; msgId: string }) => Promise<CampaignFileResult>
  onChange: (item: IVRCampaignItem) => void
  onMicAccessError: () => void
  saveFile: (payload: { item: FileUploadType; msgId: string }) => Promise<CampaignFileResult>
}

const ReconnectContainer: React.FC<Props> = ({
  countriesTimezones,
  countryCode,
  groups,
  item,
  itemId,
  loading,
  orgId,
  segments,
  timezone,
  timezones,
  token,
  voiceList,
  getFile,
  onChange,
  onMicAccessError,
  saveFile,
}) => {
  const [voicePlayingId, setVoicePlayingId] = React.useState<string>()

  const { files } = useCampaignFiles()
  const messages = useSelector(selectMessagesState) as unknown as MessagesState

  const getFileHandler = React.useCallback((msgId: string, file: string) => getFile({ file, msgId }), [getFile])
  const handleConfigChange = React.useCallback(
    ({ reconnectMax, reconnectOnProgress }: { reconnectMax: number; reconnectOnProgress: boolean }) => {
      const newItem = {
        ...item,
        reconnectMax,
        reconnectOnProgress,
      }
      if (equals(item, newItem)) {
        return
      }
      onChange({
        ...item,
        reconnectMax,
        reconnectOnProgress,
      })
    },
    [item, onChange],
  )
  const saveFileHandler = React.useCallback(
    (msgId: string, file: FileUploadType) =>
      saveFile({
        item: file,
        msgId,
      }),
    [saveFile],
  )

  const handleActionsChange = React.useCallback(
    (actions: ConditionActions[]) => {
      onChange({
        ...item,
        variables: {
          ...item.variables,
          reconnectActions: actions,
        },
      })
    },
    [item, onChange],
  )
  const getImageProps = React.useCallback(
    (qid: string) => ({
      id: qid,
      loading: files.loadingItems.indexOf(qid) > -1,
      getFile: (file: string) => getFileHandler(qid, file),
      saveFileHandler: (file: FileUploadType) => saveFileHandler(qid, file),
    }),
    [files.loadingItems, getFileHandler, saveFileHandler],
  )
  const getVoiceProps = React.useCallback(
    (qid: string) => ({
      id: qid,
      enableTTS: true,
      files: files.data || {},
      filterType: item.type || '',
      loading: files.loadingItems.indexOf(qid) > -1,
      personalizationList: messages.personalizationList,
      playId: voicePlayingId,
      source: files.data,
      voiceList,
      getFile: (file: string) => getFileHandler(qid, file),
      onMicAccessError,

      saveFileHandler: (file: FileUploadType) => saveFileHandler(qid, file),
      setPlaying: (val: string) => {
        setVoicePlayingId(val)
      },
    }),
    [
      files.data,
      files.loadingItems,
      item,
      messages.personalizationList,
      voiceList,
      voicePlayingId,
      getFileHandler,
      onMicAccessError,
      saveFileHandler,
    ],
  )

  if (loading || (!!itemId && !item.type)) {
    return null
  }

  return (
    <Reconnect
      actions={pathOr(defaultActions, ['variables', 'reconnectActions'], item)}
      campaignType={item.type}
      countriesTimezones={countriesTimezones}
      countryCode={countryCode}
      defaultLanguage={item.uiStore?.defaultLanguage || ''}
      files={files.data}
      groups={groups}
      languages={item.uiStore?.languages || []}
      orgId={orgId}
      personalizationList={messages.personalizationList}
      reconnectMax={item.reconnectMax}
      reconnectOnProgress={item.reconnectOnProgress}
      senderIds={messages.senderIds}
      segments={segments}
      timezone={timezone}
      timezones={timezones}
      token={token}
      getImageProps={getImageProps}
      getVoiceProps={getVoiceProps}
      onActionsChange={handleActionsChange}
      onConfigChange={handleConfigChange}
    />
  )
}

const defaultActions = [{ when: null, actions: [] }]

export default ReconnectContainer
